import reduce from 'lodash/reduce';
import includes from 'lodash/includes';
import startsWith from 'lodash/startsWith';
import get from 'lodash/get';
import type { Request } from 'express';

import { reportZac3Conversion } from './partner_conversions/zac3';
import { reportPerformCBConversion } from './partner_conversions/performcb';
import { reportRateMarketPlaceConversion } from './partner_conversions/rate_marketplace';
import { reportJobbleConversion } from './partner_conversions/jobble';
import type {
  ConversionEvent as OrigConversionEvent,
  PartnerData as OrigPartnerData,
  PartnerName as OrigPartnerName,
  ReportConversionFunction,
} from './types';
import { InternalSource, PARTNER_PAID_CHANNEL, PartnerParams as OrigPartnerParams } from './types';
import { JERRY_ANALYTICS_INTERNAL_SOURCE } from './common';

export type PartnerData = OrigPartnerData;
export type PartnerName = OrigPartnerName;
export const PartnerParams = OrigPartnerParams;
export type ConversionEvent = OrigConversionEvent;

declare global {
  interface Window {
    jasid?: string;
  }
}

/**
 * Returns all the whitelisted params for the partner
 *
 * @param partner
 */
const getPartnerData = (partner: PartnerName, params: Record<string, string>) => {
  return reduce(
    [...PartnerParams.global, ...(PartnerParams[partner] || [])],
    (memo, item) => {
      return params[item]
        ? {
            ...memo,
            [item]: params[item],
          }
        : memo;
    },
    {} as Record<string, string>
  );
};

/**
 * Get referrer partner data from url query string or cookie or session.
 * Check url query params first.
 * Check cookie value for 'jrdat' exists and is not empty ('{}'), grab it from cookie,
 * Check session data.
 */
export const getPartnerDataJerry2 = (req: Request): { partner?: string; data?: Record<string, string> } => {
  try {
    // Check URL query first
    let jrcod = String(req.query?.utm_source || (req.query?.code ? `u${req.query.code}` : ''));
    let partnerName = getPartnerCode(jrcod);

    if (jrcod) {
      if (!partnerName) return {};
      const jrdat = getPartnerDataByCode(partnerName, req.query as Record<string, string>);
      return {
        partner: partnerName,
        data: jrdat,
      };
    }

    // Check cookie.
    jrcod = req.cookies?.jrcod;
    if (jrcod) {
      const jrdat = req.cookies?.jrdat ? JSON.parse(req.cookies?.jrdat) : {};
      partnerName = getPartnerCode(jrcod);
      if (!partnerName) return {};

      return { partner: partnerName, data: jrdat };
    }

    // Check session
    const session = get(req, 'session');
    partnerName = session?.partner?.partner;
    if (partnerName && isPartnerName(partnerName)) {
      return {
        partner: partnerName,
        data: session?.partner?.data,
      };
    }

    return {};
  } catch (e) {
    if (e.name === 'SyntaxError') return {};
    throw e;
  }
};

/**
 * Type guard to cast string to `PartnerName`
 */
const isPartnerName = (code: string | undefined): code is PartnerName => {
  return code ? !!PartnerParams[code] : false;
};

/**
 * Interprets the partner code and returns name of partner.
 * Interprets user referral codes as partner = 'user'.
 *
 * @param jrcod - code
 */
export const getPartnerCode = (jrcod: string): PartnerName | undefined => {
  // If the code starts `u` it is a user referral.
  if (jrcod.match(/^u/)) {
    return 'user';
  }

  // Otherwise it is a partner referral.
  const partnerName = includes(['facebook', 'youtube'], jrcod) ? jrcod : jrcod.replace(/^p/, '');
  return isPartnerName(partnerName) ? partnerName : undefined;
};

/**
 * Interprets the partner code and returns name of partner.
 * Interprets user referral codes as partner = 'user'.
 *
 * @param jrcod - code
 */
const getPartnerFromCode = (jrcod: string): PartnerName | undefined => {
  // If the code starts `u` it is a user referral.
  if (jrcod.match(/^u/)) {
    return 'user';
  }

  let partnerName: string | undefined = undefined;

  if (includes(PARTNER_PAID_CHANNEL, jrcod)) {
    partnerName = jrcod;
  } else if (jrcod.match(/^p/)) {
    partnerName = jrcod.replace(/^p/, '');
  }

  return isPartnerName(partnerName) ? partnerName : undefined;
};

/**
 * Returns all the whitelisted params for the partner
 *
 * @param partner
 */
export const getPartnerDataByCode = (partner: PartnerName, params: Record<string, string>): Record<string, string> => {
  return reduce(
    [...PartnerParams.global, ...(PartnerParams[partner] || [])],
    (memo, item) => {
      return params[item]
        ? {
            ...memo,
            [item]: params[item],
          }
        : memo;
    },
    {} as Record<string, string>
  );
};

/**
 * Checks referrer source and returns partner and data.
 *
 * @param query - URL query params
 * @param cookie - If calling on server-side, pass explicit cookie map.
 * @param referrer - headers.referrer
 */
export const getPartnerReferrer = (
  query: Record<string, string>,
  cookie: Record<string, string>,
  referrer: string
): PartnerData | null => {
  // Check URL params first.
  let jrcod = query.utm_source || (query.code ? `u${query.code}` : '') || (query.partner ? `p${query.partner}` : '');
  let partnerName = getPartnerFromCode(jrcod);

  if (jrcod) {
    if (!partnerName) return null;
    const jrdat = getPartnerData(partnerName, query);
    return {
      code: jrcod,
      partner: partnerName,
      data: jrdat,
    };
  }

  // Check cookies next.
  const cookies = cookie;
  jrcod = cookies.jrcod;
  const jrdatStr = cookies.jrdat;
  if (jrcod) {
    const jrdat = jrdatStr ? JSON.parse(decodeURIComponent(jrdatStr)) : {};
    partnerName = getPartnerFromCode(jrcod);
    if (!partnerName) return null;

    return {
      code: jrcod,
      partner: partnerName,
      data: jrdat,
    };
  }

  // Next, check if the document referrer is Facebook:
  if (referrer.match(/https?:\/\/(.+?\.)?facebook\.com*/)) {
    return {
      code: 'facebook',
      partner: 'facebook',
      data: {},
    };
  }

  // We store 'indirect' facebook referral in the `jrsrc` cookie. Check that value next:
  // TODO: Are we storing any other referrer (other than facebook)?
  if (cookies.jrsrc && cookies.jrsrc.match(/https?:\/\/(.+?\.)?facebook\.com*/)) {
    return {
      code: 'facebook',
      partner: 'facebook',
      data: {},
    };
  }

  return null;
};

/**
 * Get publisher from URL query params
 */
export const getPartnerPublisher = (query: Record<string, string>): string | undefined => {
  return query?.utm_publisher?.trim?.();
};

const searchEngineHosts = ['google', 'bing', 'yahoo', 'duckduckgo'];
const seoRoutes = [
  '/advice',
  '/car-insurance',
  '/questions',
  '/tag',
  '/insights',
  '/newsroom',
  '/car-loan',
  '/home-insurance',
  '/renters-insurance',
  '/driving',
];
const seoStrictRoutes = ['/', '/how-it-works', '/about', '/team', '/investers', '/careers'];
/**
 * Checks internal source, for signup
 */
export const getInternalSource = (
  query: Record<string, string>,
  cookie: Record<string, string>,
  referrer: string
): InternalSource => {
  if (cookie?.[JERRY_ANALYTICS_INTERNAL_SOURCE] === InternalSource.SEO) {
    return InternalSource.SEO;
  }
  if (!referrer) {
    return InternalSource.Default;
  }
  if (searchEngineHosts.some((v) => includes(referrer, v))) {
    return InternalSource.SEO;
  }

  let parsedReferrer: URL;
  try {
    parsedReferrer = new URL(referrer);
  } catch (err) {
    return InternalSource.Default;
  }

  if (startsWith(parsedReferrer.href, process.env.NEXT_PUBLIC_HOST)) {
    if (seoRoutes.some((v) => includes(parsedReferrer.pathname, v))) {
      return InternalSource.SEO;
    }
    if (seoStrictRoutes.some((v) => parsedReferrer.pathname === v)) {
      return InternalSource.SEO;
    }
  }

  return InternalSource.Default;
};

/**
 * Strips the `u` and `p` prefix from the referral code.
 *
 * @param code
 */
export const stripCodePrefix = (code: string): string => {
  return code.replace(/^[up]/, '');
};

/**
 * Partner conversion events.
 */
export const ReportConversionFunctions: {
  [key in PartnerName]?: ReportConversionFunction;
} = {
  zac3: reportZac3Conversion,
  performcb: reportPerformCBConversion,
  ratemarketplace: reportRateMarketPlaceConversion,
  // empowerdirect: reportEmpowerConversion,
  jobble: reportJobbleConversion,
};

/**
 * Report conversion events to partners.
 *
 * @param partnerData
 */
export const reportConversion = (event: ConversionEvent, partnerData: PartnerData): void => {
  // Only call on client side.
  if (typeof window === 'undefined') return;

  const reportFunc = ReportConversionFunctions[partnerData.partner];
  if (reportFunc) {
    reportFunc(event, partnerData, window.jasid);
  }
};
